import * as R from 'ramda';
import { useDispatch, useSelector } from 'react-redux';
import React, { useState, useEffect, useCallback } from 'react';
// common
import { makeSelectOrderTable } from '../common/idb/order/selectors';
import { getReportTableOrder, updateTableOrderByGuid } from '../common/idb/order/actions';
// components
import { openModal, closeModal } from '../components/modal/actions';
// helpers/constants
import * as G from '../helpers';
// icons
import * as I from '../svgs';
// ui
import { Box, Flex } from '../ui';
// component edit-view
import { EditViewForm } from '../components/';
//////////////////////////////////////////////////

const mainDarkColor = G.getTheme('colors.dark.mainDark');

export const getReportFieldNames = R.compose(
  R.pluck('name'),
  R.sortBy(R.prop('sequence')),
  R.pathOr([], ['fields']),
);

export const setReportFieldNames = (report, fieldNames) => {
  const currentFields = R.pathOr([], ['fields'], report);

  const fields = G.mapIndexed((name, index) => {
    const currentField = R.find(R.propEq(name, 'name'), currentFields);

    return {
      ...currentField,
      name,
      sequence: R.inc(index),
    };
  }, fieldNames);

  return R.assoc('fields', fields, report);
};

const useEditViewHook = (props) => {
  const {
    title,
    report,
    boxStyles,
    usedReport,
    titleStyles,
    columnSettings,
    updateCallback,
    defaultSelected,
    disableFieldHandler,
    updateUsedReportFields,
    useReportFields = false,
    defaultReportFields = [],
    setUsedReportWithoutReset,
  } = props;

  const dispatch = useDispatch();

  const [start, setStart] = useState(true);

  const fieldsTable = useSelector(makeSelectOrderTable());

  const reportGuid = G.getGuidFromObject(report);

  const editViewTitle = R.prop('editViewTitle', report);

  useEffect(() => {
    if (reportGuid) dispatch(getReportTableOrder(reportGuid));
  }, [reportGuid]);

  useEffect(() => {
    if (start) {
      const table = R.prop(reportGuid, fieldsTable);

      if (R.isNil(table)) return;

      setStart(false);
    }
  }, [start, fieldsTable]);

  const fields = R.propOr([], reportGuid, fieldsTable);

  const defaultUiReportFields = G.isNotNilAndNotEmpty(defaultSelected) ? defaultSelected : defaultReportFields;

  const storedUiReportFields = G.isNilOrEmpty(fields) ? defaultUiReportFields : fields;

  const reportFieldNames = getReportFieldNames(usedReport);

  const fieldsToUse = R.and(useReportFields, G.isNotNilAndNotEmpty(reportFieldNames))
    ? reportFieldNames : storedUiReportFields;

  const uiReportFields = R.filter(
    (name) => R.includes(name, defaultReportFields),
    fieldsToUse,
  );

  useEffect(() => {
    if (G.isAnyTrue(
      start,
      R.not(useReportFields),
      G.isNilOrEmpty(usedReport),
      G.isNilOrEmpty(uiReportFields),
      G.isNotNilAndNotEmpty(reportFieldNames),
      R.not(G.isFunction(setUsedReportWithoutReset)),
    )) return;

    setUsedReportWithoutReset(setReportFieldNames(usedReport, uiReportFields));
  }, [
    start,
    usedReport,
    uiReportFields,
    useReportFields,
    reportFieldNames,
    setUsedReportWithoutReset,
  ]);

  const setUiReportFields = useCallback((reportFields) => {
    const { guid } = report;

    if (useReportFields) {
      return G.callFunctionWithArgs(
        updateUsedReportFields,
        setReportFieldNames(usedReport, reportFields),
      );
    }

    dispatch(updateTableOrderByGuid({
      updateCallback,
      reportGuid: guid,
      orderTable: reportFields,
    }));
  }, [report, usedReport, useReportFields, updateUsedReportFields]);

  const resetUiReportFields = useCallback(() => {
    const { guid } = report;

    if (useReportFields) {
      G.callFunctionWithArgs(
        updateUsedReportFields,
        setReportFieldNames(usedReport, defaultUiReportFields),
      );

      return dispatch(closeModal());
    }

    dispatch(updateTableOrderByGuid({
      updateCallback,
      orderTable: [],
      reportGuid: guid,
    }));

    dispatch(closeModal());
  }, [
    report,
    usedReport,
    useReportFields,
    defaultUiReportFields,
    updateUsedReportFields,
  ]);

  const handleOpenEditView = useCallback(() => {
    const component = (
      <EditViewForm
        report={report}
        reportGuid={reportGuid}
        columnSettings={columnSettings}
        uiReportFields={uiReportFields}
        setUiReportFields={setUiReportFields}
        disableFieldsStorage={useReportFields}
        resetUiReportFields={resetUiReportFields}
        disableFieldHandler={disableFieldHandler}
        closeModal={() => dispatch(closeModal())}
        defaultReportFields={defaultReportFields}
      />
    );

    const modal = {
      p: '0px',
      component,
      options: {
        width: 500,
        top: '0px',
        right: '0px',
        movable: false,
        height: '100vh',
        componentWrapperHeight: '100%',
        title: G.getWindowLocale(R.or(editViewTitle, 'titles:fields'), 'Fields'),
      },
    };

    dispatch(openModal(modal));
  }, [
    columnSettings,
    uiReportFields,
    useReportFields,
    setUiReportFields,
    disableFieldHandler,
    resetUiReportFields,
  ]);

  const editViewBtnComponent = (
    <Flex
      gap={5}
      ml={20}
      bg='white'
      p='4px 8px'
      cursor='pointer'
      fontWeight={600}
      borderRadius={10}
      width='fit-content'
      onClick={handleOpenEditView}
      border={`1px solid ${G.getTheme('colors.borderGray')}`}
      {...G.spreadUiStyles(boxStyles)}
    >
      <Flex>{I.gear(mainDarkColor)}</Flex>
      <Box fontSize={13} color={mainDarkColor} {...G.spreadUiStyles(titleStyles)}>
        {R.or(title, G.getWindowLocale('titles:edit-view', 'Edit View'))}
      </Box>
    </Flex>
  );

  return {
    start,
    uiReportFields,
    handleOpenEditView,
    editViewBtnComponent,
  };
};

export default useEditViewHook;
